import { useRedirect, Link, type RaRecord } from "react-admin";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import { formatListDate } from "@/utils/date-format";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import {
  isOhbatRazaEvent,
  razaEventKindLabel,
  razaEventPurposeLabel,
  razaEventTitle,
} from "@/utils/raza-event-display";
import {
  formatRazaEventVenueForDisplay,
  resolveRazaEventVenueFromRecord,
} from "@/utils/raza-event-venue-display";
import {
  eventNeedsAnySadarat,
  formatRazaEventSadaratListLabel,
} from "@/utils/format-raza-event-sadarat-display";

function LabelValue({
  label,
  value,
  grid,
}: {
  label: string;
  value: React.ReactNode;
  grid?: number;
}) {
  return (
    <Grid size={{ xs: 12, md: grid ?? 6 }}>
      <Typography fontWeight="bold">{label}</Typography>
      <Typography component="div">{value}</Typography>
    </Grid>
  );
}

type RazaEventsDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  selectedEvent: RaRecord | null;
};

export function RazaEventsDetailsDialog({
  open,
  onClose,
  selectedEvent,
}: RazaEventsDetailsDialogProps) {
  const redirect = useRedirect();
  const isOhbat = selectedEvent ? isOhbatRazaEvent(selectedEvent) : true;
  const venueDisplay = selectedEvent
    ? formatRazaEventVenueForDisplay(
        resolveRazaEventVenueFromRecord({
          address: selectedEvent.address as string | null | undefined,
          hallBookings: selectedEvent.hallBookings as
            | Array<{ hall?: { venue?: string | null } | null }>
            | undefined,
        })
      )
    : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }} py={1}>
        <Typography variant="h6" alignSelf="center">
          {selectedEvent ? razaEventTitle(selectedEvent) : "Raza event"}
        </Typography>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {selectedEvent && (
          <Box>
            <Grid container spacing={2}>
              <LabelValue label="Date" value={formatListDate(selectedEvent.date as string)} />
              <LabelValue
                label="Time"
                value={formatRazaEventStartTimeLabel(
                  selectedEvent?.startTime as string | undefined
                )}
              />
              {!isOhbat && (
                <>
                  <LabelValue label="Title" value={String(selectedEvent?.title ?? "—")} />
                  <LabelValue label="Purpose" value={razaEventPurposeLabel(selectedEvent) || "—"} />
                </>
              )}
              {isOhbat && <LabelValue label="Kind" value={razaEventKindLabel(selectedEvent)} />}
              <LabelValue label="Host ITS" value={String(selectedEvent?.hostItsNo ?? "—")} />
              <LabelValue label="Host name" value={String(selectedEvent?.hostName ?? "—")} />
              <LabelValue label="Sector" value={String(selectedEvent?.hostSector ?? "—")} />
              <LabelValue label="Sub-sector" value={String(selectedEvent?.hostSubSector ?? "—")} />
              <LabelValue label="Contact mobile" value={String(selectedEvent?.mobileNo ?? "—")} />
              <LabelValue
                label="Venue address"
                value={
                  <>
                    {venueDisplay?.value ?? "—"}
                    {venueDisplay?.hint ? (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {venueDisplay.hint}
                      </Typography>
                    ) : null}
                  </>
                }
                grid={12}
              />
              {eventNeedsAnySadarat(selectedEvent) && (
                <LabelValue
                  label="Sadarat"
                  value={formatRazaEventSadaratListLabel(selectedEvent)}
                  grid={12}
                />
              )}
              {isOhbat && (
                <>
                  <LabelValue
                    label="Khidmatguzar name"
                    value={String(
                      (selectedEvent?.khidmatguzar as { Full_Name?: string } | undefined)
                        ?.Full_Name ?? "—"
                    )}
                  />
                  <LabelValue
                    label="Khidmatguzar mobile"
                    value={String(
                      (selectedEvent?.khidmatguzar as { Mobile?: string } | undefined)?.Mobile ??
                        "—"
                    )}
                  />
                  <LabelValue
                    label="Zakereen name"
                    value={String(
                      (selectedEvent?.zakereen as { Full_Name?: string } | undefined)?.Full_Name ??
                        "—"
                    )}
                  />
                  <LabelValue
                    label="Zakereen mobile"
                    value={String(
                      (selectedEvent?.zakereen as { Mobile?: string } | undefined)?.Mobile ?? "—"
                    )}
                  />
                </>
              )}
              {selectedEvent.razaRequestId && (
                <LabelValue
                  label="Raza request"
                  value={
                    <Link to={`/razaRequests/${String(selectedEvent.razaRequestId)}/show`}>
                      View request
                    </Link>
                  }
                  grid={12}
                />
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1, flexWrap: "wrap" }}>
        <Button
          onClick={() => {
            if (selectedEvent?.id == null) return;
            redirect("show", "raza-events", selectedEvent.id);
            onClose();
          }}
          variant="outlined"
        >
          Show
        </Button>
        <Button
          onClick={() => {
            if (selectedEvent?.id == null) return;
            redirect("edit", "raza-events", selectedEvent.id);
            onClose();
          }}
          variant="contained"
        >
          Edit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
